import * as React from 'react';
import { debounce } from '@packages/common';

import { breakpointSizes, BreakpointNames } from '../../theme';

const breakpointsSizeMap = Object.keys(breakpointSizes).reduce((acc, next) => {
  return { ...acc, [next]: Number((breakpointSizes as any)[next].replace('px', '')) };
}, {} as { [key in BreakpointNames]: number });

interface BreakpointMountContextValue {
  currentBreakpoint: BreakpointNames;
}

function getBreakpointFloor(bp: BreakpointNames): number {
  const breakpointNames = Object.keys(breakpointSizes);
  const bpIdx = breakpointNames.indexOf(bp);
  if (bpIdx < 1) {
    return 0;
  }

  const prevBp = breakpointNames[bpIdx - 1] as BreakpointNames;
  return breakpointsSizeMap[prevBp];
}

export function shouldRender(
  currentBreakpoint: BreakpointNames,
  config: {
    min?: BreakpointNames;
    max?: BreakpointNames;
  },
): boolean {
  const { min, max } = config;
  const currentBreakpointFloor = getBreakpointFloor(currentBreakpoint);

  // the current breakpoint actually holds the next breakpoint. so if we have a
  // screen size below 480px, let's say 420, `currentBreakpoint` will return
  // 480. if we are at 480 it will also be 480, but at 481 it will be 768
  const currentBreakpointCeiling = breakpointsSizeMap[currentBreakpoint];
  const startFrom = min ? getBreakpointFloor(min) : 0;
  let shouldRender = startFrom <= currentBreakpointFloor;
  if (max) {
    const endAt: number = breakpointsSizeMap[max];
    shouldRender = shouldRender && currentBreakpointCeiling <= endAt;
  }

  return shouldRender;
}

const getCurrentBreakpoint = () => {
  return Object.keys(breakpointsSizeMap)
    .reverse()
    .reduce((acc, next) => {
      // < instead of <=, beause the upper bound does not include the
      // breakpoint-size (md goes from 480px to 767px, not to 768px)
      return window.innerWidth < Number((breakpointsSizeMap as any)[next]) ? next : acc;
    }, BreakpointNames.xxl);
};

export const BreakpointMountContext = React.createContext({} as BreakpointMountContextValue);

interface BreakpointMountProviderProps {}

export const BreakpointMountProvider: React.FC<BreakpointMountProviderProps> = ({ children }) => {
  const [currentBreakpoint, setBreakpoint] = React.useState(BreakpointNames.xxl);

  React.useEffect(() => {
    const handleResize = debounce(() => {
      const nextBreakpoint = getCurrentBreakpoint() as BreakpointNames;
      if (currentBreakpoint !== nextBreakpoint) {
        setBreakpoint(nextBreakpoint);
      }
    }, 200);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentBreakpoint]);

  return <BreakpointMountContext.Provider value={{ currentBreakpoint }}>{children}</BreakpointMountContext.Provider>;
};

interface BreakpointMountProps {
  min?: BreakpointNames;
  max?: BreakpointNames;
}

/**
 * @description max is inclusive. Setting max to undefined will make children being displayed open-ended.
 * @example
 * <BreakpointMount max={BreakpointNames.md}>stuff</BreakpointMount> will show `stuff` up to and including md (so from 0px up to 767px)
 * <BreakpointMount min={BreakpointNames.md} max={BreakpointNames.lg}>stuff</BreakpointMount> will show `stuff` from md on (480px) up to and including lg (so up to 767px)
 * <BreakpointMount min={BreakpointNames.md} max={BreakpointNames.md}>stuff</BreakpointMount> will show `stuff` only for md (480px to 767px)
 */
export const BreakpointMount: React.FC<BreakpointMountProps> = ({ children, min, max }) => {
  const { currentBreakpoint } = React.useContext(BreakpointMountContext);
  return <>{shouldRender(currentBreakpoint, { min, max }) ? children : null}</>;
};
